import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { toast } from "sonner";
import { updateManualReconciliationData } from "@/Api/reconcialiationFinancialTransactions";
import { Loader2, PencilLine } from "lucide-react";
import { useState } from "react";
import { FinancialReconciliationStatus } from "./Table";
import { Mask } from "@/utils/mask";

const formSchema = z.object({
  reconciled_amount: z.string().min(1, "Valor é obrigatório"),
  source_description: z.string().optional(),
  reconciliation_status_id: z.string().min(1, "Status é obrigatório"),
});

interface ManualReconciliationProps {
  uuid: string;
  originalAmount: number;
  onSuccess: () => void;
  sourceDescription?: string;
}

export function ManualReconciliation({
  uuid,
  originalAmount,
  sourceDescription,
  onSuccess,
}: ManualReconciliationProps) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      reconciled_amount: String(Mask.formatCurrency(originalAmount)),
      source_description: sourceDescription || "",
      reconciliation_status_id: String(FinancialReconciliationStatus.APPROVED),
    },
  });

  const onSubmit = async (values: z.infer<typeof formSchema>) => {
    try {
      setLoading(true);

      const reconciledAmount = Number(
        Mask.transformCurrencyInFloat(values.reconciled_amount)
      );

      const { data, ok } = await updateManualReconciliationData(uuid, {
        reconciled_amount: reconciledAmount,
        source_description: values?.source_description || "",
        reconciliation_status_id: Number(values?.reconciliation_status_id),
      });

      if (ok) {
        toast.success(
          data?.message || "Conciliação manual realizada com sucesso!"
        );
        onSuccess();
        setOpen(false);
      } else {
        toast.error(data?.message || "Erro ao realizar conciliação manual!");
      }
    } catch (error) {
      console.error(error);
      toast.error("Ocorreu um erro ao processar sua solicitação.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="size-7 p-0 text-slate-900 hover:bg-stone-950/10 dark:text-[#F4F4F5]"
        >
          <PencilLine className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Conciliação Manual</DialogTitle>
          <DialogDescription>
            Realize a conciliação manual da transação. Valor Original:{" "}
            {Mask.formatCurrency(originalAmount)}
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="reconciled_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Valor Conciliado</FormLabel>
                  <FormControl>
                    <Input
                      typeMask="currency"
                      placeholder="R$ 0,00"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="source_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descrição da Fonte</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Descreva a fonte da conciliação"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="reconciliation_status_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Status</FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione um status" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                    <SelectItem
                        value={String(
                          FinancialReconciliationStatus.APPROVED
                        )}
                      >
                        Aprovado
                      </SelectItem>
                      <SelectItem
                        value={String(
                          FinancialReconciliationStatus.APPROVED_MANUAL
                        )}
                      >
                        Aprovado Manualmente
                      </SelectItem>
                      <SelectItem
                        value={String(FinancialReconciliationStatus.DIVERGENT)}
                      >
                        Divergente
                      </SelectItem>
                      <SelectItem
                        value={String(FinancialReconciliationStatus.REJECTED)}
                      >
                        Rejeitado
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end">
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Conciliar
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
